import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track, getUTMParams } from "@/lib/analytics";
import { CLUB_MEMBER_KEY } from "./ClubPopup";

export default function ClubSection() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    setError("");
    track("club_signup_start", {});
    try {
      const utm = getUTMParams();
      await api.post("/club/subscribe", {
        email,
        marketing_consent: consent,
        consent_version: "1.0",
        source: "website",
        ...utm,
      });
      setStatus("success");
      localStorage.setItem(CLUB_MEMBER_KEY, "1");
      track("club_signup_success", {});
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Riprova più tardi.");
      setStatus("error");
    }
  };

  return (
    <section id="club" className="relative bg-[#0C0C0C] grain py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10 text-center">
        <span className="font-display font-bold uppercase tracking-[0.4em] text-sm text-[#FFC20E]">UNGRIP CLUB</span>

        <AnimatePresence mode="wait">
          {status !== "success" ? (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -10 }}>
              <h2 className="font-display font-black uppercase display-tight text-5xl sm:text-7xl text-white mt-3">
                NON TUTTI<br />DEVONO SAPERE TUTTO.
              </h2>
              <p className="mt-5 text-white/70 text-lg max-w-xl mx-auto">
                Chi è dentro lo sa prima. Offerte riservate, drop, limited edition e nuovi morsi in anteprima.
              </p>

              <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="LA TUA EMAIL"
                  className="flex-1 rounded-full bg-[#141414] border border-[#333] px-5 py-3.5 text-white placeholder:text-white/40 font-display font-bold uppercase tracking-wide outline-none focus:border-[#FFC20E] transition-colors"
                  data-testid="club-email-input"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="rounded-full bg-[#FFC20E] text-[#0C0C0C] font-display font-extrabold uppercase tracking-wide px-7 py-3.5 hover:scale-[1.03] transition-transform duration-200 disabled:opacity-60"
                  data-testid="club-submit-button"
                >
                  {status === "loading" ? "…" : "FAMMI ENTRARE"}
                </button>
              </form>

              <label className="mt-4 flex items-start gap-2 justify-center text-white/50 text-xs max-w-md mx-auto cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-[#FFC20E]"
                  data-testid="club-consent-checkbox"
                />
                <span>Acconsento a ricevere comunicazioni marketing da UNGRIP secondo la Privacy Policy (GDPR).</span>
              </label>

              {error && <p className="mt-3 text-[#E63946] text-sm" data-testid="club-error">{error}</p>}
              <p className="mt-4 font-hand text-2xl text-[#FFC20E]">Niente spam. Solo roba che vale la pena mordere.</p>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-6"
              data-testid="club-success-message"
            >
              <h2 className="font-display font-black uppercase display-tight text-6xl sm:text-8xl text-[#FFC20E] mt-3">
                SEI DENTRO.
              </h2>
              <p className="mt-5 text-white/80 text-xl max-w-lg mx-auto">
                Adesso sai cose che gli altri non sanno ancora.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
